/**
 * Client module entitlements: "did this client buy this?" (permissions.ts is what a user may do inside one).
 * Keys mirror the backend catalogue; the dot is namespacing, never authorisation: `ap` does NOT grant `ap.payments`.
 */
export const MODULE_KEYS = [
  "ar",
  "ar.payments",
  "ar.e_invoicing",
  "ar.accounting",
  "ap",
  "ap.payments",
  "ap.e_invoicing",
  "ap.accounting",
  "expenses",
  "whatsapp",
  "banking",
  "books",
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

// Any-of, mirroring the backend: one accounting connection, and one e-invoicing registration (which sends and
// receives), serves every module listed.
export const ACCOUNTING_CONNECTION_MODULES = ["expenses", "ar.accounting", "ap.accounting"] as const satisfies readonly ModuleKey[];

export const E_INVOICING_REGISTRATION_MODULES = ["ap.e_invoicing", "ar.e_invoicing"] as const satisfies readonly ModuleKey[];

const MODULE_LABELS: Record<ModuleKey, string> = {
  ar: "Receivables",
  "ar.payments": "Invoice payments",
  "ar.e_invoicing": "E-invoice sending",
  "ar.accounting": "Invoice accounting sync",
  ap: "Payables",
  "ap.payments": "Bill payments",
  "ap.e_invoicing": "E-invoice receiving",
  "ap.accounting": "Bill accounting sync",
  expenses: "Expenses",
  whatsapp: "WhatsApp messaging",
  banking: "Bank connections",
  books: "Books",
};

/** Commercial name for a module key, falling back to the raw key if it is unknown here. */
export function moduleLabel(key: string): string {
  return MODULE_LABELS[key as ModuleKey] ?? key;
}

/** Toast and lock-overlay copy. Pass several keys where any one would do. */
export function moduleForbiddenMessage(key: string | readonly string[] | null): string {
  const keys = key === null ? [] : typeof key === "string" ? [key] : key;
  return keys.length > 0
    ? `Forbidden - ${keys.map(moduleLabel).join(" or ")} is not included in your plan. Contact your Dolfin account manager to request access.`
    : "Forbidden - that feature is not included in your plan. Contact your Dolfin account manager to request access.";
}

/** Copy for the "Request access" affordance, where the block itself is already on screen. */
export function moduleRequestAccessMessage(key: string): string {
  return `${moduleLabel(key)} is not included in your plan. Contact your Dolfin account manager to have it enabled.`;
}
